import React, { useState } from 'react';
import { Flex } from '@chakra-ui/react';
import { OutlineCard, Switch, useToast } from '@morressier/alembic';
import { useUpdateJournalSettings } from 'src/api/mutations';
import { FieldLabel } from 'src/components/Field';

interface FeatureFlagsTabProps {
  journalId: string;
  journalName: string;
  isUADEnable?: boolean;
  enableConsolidatedPdf?: boolean;
  notifyInvitingEditorsOnReviewTargetMet?: boolean;
}

type FeatureFlagKey =
  | 'isUADEnable'
  | 'assignReviewers'
  | 'enableConsolidatedPdf'
  | 'notifyInvitingEditorsOnReviewTargetMet';

interface FeatureFlagSwitchProps {
  label: string;
  subText: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

const FeatureFlagSwitch: React.FC<FeatureFlagSwitchProps> = ({
  label,
  subText,
  checked,
  onChange
}) => (
  <OutlineCard borderless paddingX={4} paddingY={6}>
    <Flex
      alignItems={{ base: 'flex-start', md: 'center' }}
      justifyContent="space-between"
      gap={4}
    >
      <FieldLabel label={label} subText={subText} isRequired={false} />
      <Switch
        isChecked={checked}
        onChange={event => onChange(event.target.checked)}
      />
    </Flex>
  </OutlineCard>
);

const FeatureFlagsTab: React.FC<FeatureFlagsTabProps> = ({
  journalId,
  isUADEnable,
  enableConsolidatedPdf,
  notifyInvitingEditorsOnReviewTargetMet
}) => {
  const toast = useToast();
  const updateSettingMutation = useUpdateJournalSettings(journalId);

  const [isUADEnabled, setIsUADEnabled] = useState(!!isUADEnable);
  const [, setIsConsolidatedPdfEnabled] = useState(
    !!enableConsolidatedPdf
  );
  const [notifyOnReviewTargetMet, setNotifyOnReviewTargetMet] = useState(
    !!notifyInvitingEditorsOnReviewTargetMet
  );

  const handleToggle = async (
    key: FeatureFlagKey,
    label: string,
    checked: boolean
  ) => {
    if (key === 'isUADEnable') setIsUADEnabled(checked);
    if (key === 'enableConsolidatedPdf') setIsConsolidatedPdfEnabled(checked);
    if (key === 'notifyInvitingEditorsOnReviewTargetMet') {
      setNotifyOnReviewTargetMet(checked);
    }

    const res = await updateSettingMutation.mutateAsync({
      data: {},
      config: { [key]: checked }
    } as any);

    if (res.isSuccess) {
      toast({
        variant: 'success',
        title: `${label} ${checked ? 'enabled' : 'disabled'}`,
        isClosable: true
      });
      return;
    }

    // Revert on failure
    if (key === 'isUADEnable') setIsUADEnabled(!checked);
    if (key === 'enableConsolidatedPdf') setIsConsolidatedPdfEnabled(!checked);
    if (key === 'notifyInvitingEditorsOnReviewTargetMet') {
      setNotifyOnReviewTargetMet(!checked);
    }
    toast({
      variant: 'error',
      title:
        typeof res?.message === 'string' ? res.message : 'Something went wrong',
      isClosable: true
    });
  };

  return (
    <Flex flexDirection="column" gap={4} width="100%">
      <FeatureFlagSwitch
        label="Anomaly Detection"
        subText="Enable anomaly detection for submissions."
        checked={isUADEnabled}
        onChange={checked =>
          handleToggle('isUADEnable', 'Anomaly Detection', checked)
        }
      />
      <FeatureFlagSwitch
        label="Review Target Met Editor Notification"
        subText="Email inviting editors when the configured number of completed review target is reached."
        checked={notifyOnReviewTargetMet}
        onChange={checked =>
          handleToggle(
            'notifyInvitingEditorsOnReviewTargetMet',
            'Review Target Met Editor Notification',
            checked
          )
        }
      />
    </Flex>
  );
};

export default React.memo(FeatureFlagsTab);
